import { useEffect, useState } from "react";
import { startDebate } from "../api.js";
import { readError, isLimitReached } from "../errors.js";
import Icon from "../ui/Icon.jsx";
import Limit from "./Limit.jsx";
import { mark } from "../funnel.js";

export default function Stance({ topic, onBack, onStarted }) {
  const [stance, setStance] = useState(null);       // "A" | "B"
  const [conviction, setConviction] = useState(50);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { mark("topicPick"); }, []);

  if (err && isLimitReached(err)) return <Limit message={err.message} />;

  const label = stance === "A" ? topic.a : stance === "B" ? topic.b : null;
  const opposite = stance === "A" ? topic.b : stance === "B" ? topic.a : null;

  async function start() {
    if (!stance || sending) return;
    setSending(true);
    setErr(null);
    try {
      // ★ 동의를 여기서 보내지 않는다(undefined → 서버에서 false 로 읽힌다).
      //   이미 동의해 둔 사람의 동의는 effectiveConsent 가 지켜 주므로 꺼지지 않는다.
      //   새로 받는 건 결과·저장 화면이 한다 — 거기서만 줄 게 있다.
      const { debateId, stanceLabel } = await startDebate(topic.id, stance, conviction);
      mark("debateStart");

      // startDebate 는 { debateId, topic, stanceLabel } 만 돌려준다.
      // 화면은 resumeDebate 가 주는 토론 문서 모양을 기대하므로 여기서 맞춰 준다.
      // 두 경로가 다른 모양을 흘리면 토론 화면이 debateId 를 잃는다 (실제로 겪었다).
      onStarted({
        id: debateId,
        topicSnapshot: { id: topic.id, title: topic.title, a: topic.a, b: topic.b, frame: topic.frame ?? "time" },
        stance,
        stanceLabel,
        convictionStart: conviction,
        convictionEnd: null,
        status: "in_progress",
      });
    } catch (e) {
      setErr(e);
      setSending(false);
    }
  }

  return (
    <main className="screen">
      <header className="bar">
        <button type="button" className="icon-btn" aria-label="주제 목록으로" onClick={onBack}>
          <Icon name="back" />
        </button>
        <span className="note">{topic.category}</span>
      </header>

      <h1 className="title">{topic.title}</h1>

      <fieldset style={{ border: 0, padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
        <legend className="label" style={{ fontSize: 16, paddingBottom: 10 }}>어느 편이에요?</legend>
        {[["A", topic.a], ["B", topic.b]].map(([key, text]) => (
          <button
            key={key}
            type="button"
            aria-pressed={stance === key}
            onClick={() => setStance(key)}
            className="btn"
            style={{
              minHeight: 62,
              justifyContent: "space-between",
              borderRadius: 4,
              fontSize: 18,
              background: stance === key ? "var(--red)" : "var(--white)",
              boxShadow: stance === key ? "4px 4px 0 var(--ink)" : "none",
            }}
          >
            <span>{text}</span>
            {stance === key && <Icon name="check" size={24} width={2.8} />}
          </button>
        ))}
        {opposite && <span className="pill ai">AI는 반대편 ‘{opposite}’에 서요</span>}
      </fieldset>

      <div className="card">
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
          <label htmlFor="conv" className="label" style={{ fontSize: 16, paddingBottom: 8 }}>
            지금 얼마나 확신해요?
          </label>
          <output htmlFor="conv" className="d num" style={{ fontSize: 56, lineHeight: 1 }}>{conviction}</output>
        </div>
        <input
          id="conv" className="rng" type="range" min="0" max="100" value={conviction}
          onChange={(e) => setConviction(Number(e.target.value))}
        />
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span className="note">0 · 반반</span>
          <span className="note">100 · 확실</span>
        </div>
        <p className="note">점수가 아니에요. 끝나고 한 번 더 물어볼게요.</p>
      </div>

      <span className="grow" />

      {err && (
        <div role="alert" className="card alert">
          <p style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.5 }}>{readError(err).title}</p>
          {readError(err).hint && <p className="note" style={{ color: "var(--ink)" }}>{readError(err).hint}</p>}
        </div>
      )}

      {/*
        ★ 동의·인구통계를 여기서 묻지 않는다.
        토론을 시작하기도 전에는 **줄 게 없다.** 「또래와 비교해드려요」는
        결과가 있어야 성립하는 약속이라, 여기서 하면 그냥 개인정보 요구로 읽힌다.
        결과 화면(PeerCompare)과 저장 시트(Save)에서 묻는다 — 거기선 보여줄 게 있다.
        소급은 서버가 한다(syncResearchScope).
      */}

      <aside className="card mark" style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}>
        <Icon name="warn" size={20} width={2.8} />
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <p style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.5 }}>누르면 오늘 몫이 나가요</p>
          <p style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.55 }}>
            하루 한 번이라, 중간에 그만둬도 오늘은 새로 못 시작해요. 대신 이어서 할 수는 있어요.
          </p>
        </div>
      </aside>

      <button type="button" className="btn go" onClick={start} disabled={!stance || sending}
        style={{ minHeight: 60, fontSize: 18 }}>
        <span>{sending ? "시작하는 중…" : label ? `${label}로 시작하기` : "편을 골라주세요"}</span>
        {!sending && stance && <Icon name="arrow" size={20} />}
      </button>
      <p className="note" style={{ textAlign: "center" }}>여섯 번 주고받아요 · 승패나 점수는 없어요</p>
    </main>
  );
}
